import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { IOrder, IProductType } from '@cakeiteasy/web-utils';

import {
  getLoginTrack,
  getLogoutTrack,
  getOrderPurchaseTrack,
  getPageTrack,
  getPaymentTrack,
  getViewItemTrack,
  getSearchTrack,
  getAddShippingInfoTrack,
  getSignUpTrack,
  getVirtualPageViewTrack, getAddToCartTrack, getBeginCheckoutTrack,
  getRemoveFromCartTrack, getViewCartTrack, getSearchNoResultTrack,
} from '../helpers';

import {
  IAddToCartPayload,
  IAddPaymentInfoPayload,
  IAddShippingInfoPayload, IPageInfo, IAuthTrackPayload, IVirtualPageViewPayload,
} from '../interfaces';
import { ICart } from '../../cart/models/cart/i-cart';
import { ICartLine } from '../../cart/models/cart/i-cart-line';


@Injectable({
  providedIn: 'root',
})
export class DataLayerService {
  protected _eventsContext: { [key: string]: string | number | boolean } = {};

  constructor(@Inject(PLATFORM_ID) protected _platformId: any) {
  }

  public registerEventContext(context: { [key: string]: string | number | boolean }) {
    this._eventsContext = context || {};
  }

  public eraseEventContext() {
    this._eventsContext = {};
  }

  async trackRemoveFromCart(cart: ICart, lines: ICartLine[]): Promise<void> {
    await this.sendEvent(...getRemoveFromCartTrack(cart, lines));
  }

  async trackViewCart(cart: ICart): Promise<void> {
    await this.sendEvent(...getViewCartTrack(cart));
  }

  async trackPage(pageInfo: IPageInfo): Promise<void> {
    await this.sendEvent(...getPageTrack(pageInfo));
  }

  async trackSignUp(payload: IAuthTrackPayload): Promise<void> {
    await this.sendEvent(...getSignUpTrack(payload));
  }

  async trackLogin(payload: IAuthTrackPayload): Promise<void> {
    await this.sendEvent(...getLoginTrack(payload));
  }

  async trackLogout(): Promise<void> {
    await this.sendEvent(...getLogoutTrack());
  }

  async trackSearch(data: { searchTerm: string, pageLocation: string }): Promise<void> {
    await this.sendEvent(...getSearchTrack(data));
  }

  async trackSearchNoResult(data: { searchTerm: string, pageLocation: string }): Promise<void> {
    await this.sendEvent(...getSearchNoResultTrack(data));
  }

  async trackVirtualPageView(payload: IVirtualPageViewPayload): Promise<void> {
    await this.sendEvent(...getVirtualPageViewTrack(payload));
  }

  async trackAddToCart(payload: IAddToCartPayload): Promise<void> {
    await this.sendEvent(...getAddToCartTrack(payload));
  }

  async trackViewItem(product: IProductType): Promise<void> {
    await this.sendEvent(...getViewItemTrack(product));
  }

  async trackAddShippingInfo(params: IAddShippingInfoPayload): Promise<void> {
    await this.sendEvent(...getAddShippingInfoTrack(params));
  }

  async trackBeginCheckout(params: IAddShippingInfoPayload): Promise<void> {
    await this.sendEvent(...getBeginCheckoutTrack(params));
  }

  async trackAddPaymentInfo(payload: IAddPaymentInfoPayload): Promise<void> {
    await this.sendEvent(...getPaymentTrack(payload));
  }

  async trackOrderPurchase(order: IOrder): Promise<void> {
    await this.sendEvent(...getOrderPurchaseTrack(order));
  }

  protected async sendEvent(event: string,
                            data: { [key: string]: number | string | boolean | object }): Promise<void> {
    if (isPlatformServer(this._platformId)) {
      return;
    }

    try {
      (window as any).dataLayer = (window as any).dataLayer || [];
      const eventData = this._buildEventData(data);

      if (eventData['ecommerce']) {
        (window as any).dataLayer.push({ ecommerce: null });
      }

      (window as any).dataLayer.push({ event, ...eventData });
    } catch (error) {
      console.error('G4 PUSH EVENT ERROR: ', error);
    }
  }

  protected _buildEventData(data: { [key: string]: number | string | boolean | object }): object {
    return {
      ...(data || {}),
      ...this._eventsContext
    };
  }
}
